import { useRef, useCallback } from 'react';
import { VoiceType, NarrativeTone, GameRules } from '@/types/game';
import { NARRATIVE_TONE_TEXTS } from '@/hooks/useNarrativeTexts';

interface PreloadedAudio {
  url: string;
  blob: Blob;
}

interface PreloadCache {
  intro: PreloadedAudio | null;
  rolesIntro: PreloadedAudio | null;
  roles: Map<string, PreloadedAudio>;
}

export const useNarrationPreloader = () => {
  const cacheRef = useRef<PreloadCache>({
    intro: null,
    rolesIntro: null,
    roles: new Map(),
  });
  const abortControllerRef = useRef<AbortController | null>(null);
  const isPreloadingRef = useRef(false);

  const fetchAudio = async (text: string, voiceType: VoiceType, signal?: AbortSignal): Promise<PreloadedAudio | null> => {
    try {
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/elevenlabs-tts`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
            Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY}`,
          },
          body: JSON.stringify({ text, voiceType }),
          signal,
        }
      );

      if (!response.ok) {
        throw new Error(`TTS request failed: ${response.status}`);
      }

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      return { url, blob };
    } catch (error) {
      if ((error as Error).name === 'AbortError') {
        console.log('Preload request aborted');
        return null;
      }
      console.error('Preload error:', error);
      return null;
    }
  };

  const preloadNarrations = useCallback(async (
    voiceType: VoiceType,
    narrativeTone: NarrativeTone,
    rules: GameRules
  ) => {
    // Cancel any existing preload
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Clear previous cache
    if (cacheRef.current.intro?.url) {
      URL.revokeObjectURL(cacheRef.current.intro.url);
    }
    if (cacheRef.current.rolesIntro?.url) {
      URL.revokeObjectURL(cacheRef.current.rolesIntro.url);
    }
    cacheRef.current.roles.forEach((audio) => {
      URL.revokeObjectURL(audio.url);
    });
    cacheRef.current = { intro: null, rolesIntro: null, roles: new Map() };

    abortControllerRef.current = new AbortController();
    const signal = abortControllerRef.current.signal;
    isPreloadingRef.current = true;

    console.log('Starting narration preload for:', voiceType, narrativeTone);

    const texts = NARRATIVE_TONE_TEXTS[narrativeTone];

    // Preload game intro (priority 1)
    const introText = texts.gameIntro();
    const introAudio = await fetchAudio(introText, voiceType, signal);
    if (introAudio) {
      cacheRef.current.intro = introAudio;
      console.log('Preloaded: game intro');
    }

    // Preload roles intro (priority 2)
    const rolesIntroTexts: Record<NarrativeTone, string> = {
      taquin: "Avant de commencer, laissez-moi vous présenter les différents rôles de cette partie ! Attention, c'est important de connaître vos adversaires !",
      coquin: "Avant de passer aux choses sérieuses, je vais vous présenter les différents rôles... et leurs petits talents cachés !",
      quebecois: "Bon ben mes amis, avant de commencer, je vais vous expliquer les rôles ! Écoutez ben comme il faut, c'est important en tabarouette !",
      ivre: "Alors... hic... avant de commencer... *burp* ... je vais vous présenter les rôles ! Essayez de suivre parce que moi j'ai déjà du mal !",
      sauvage: "Bon bande de nazes, je vais vous expliquer les rôles parce que visiblement vous pigez rien à ce jeu !",
    };

    const rolesIntroAudio = await fetchAudio(rolesIntroTexts[narrativeTone], voiceType, signal);
    if (rolesIntroAudio) {
      cacheRef.current.rolesIntro = rolesIntroAudio;
      console.log('Preloaded: roles intro');
    }

    // Preload individual role narrations based on enabled roles
    const rolesToPreload = ['werewolf', 'villager'];
    if (rules.enabledRoles.seer) rolesToPreload.push('seer');
    if (rules.enabledRoles.witch) rolesToPreload.push('witch');
    if (rules.enabledRoles.hunter) rolesToPreload.push('hunter');
    if (rules.enabledRoles.cupid) rolesToPreload.push('cupid');
    if (rules.enabledRoles.white_wolf) rolesToPreload.push('white_wolf');

    // Preload roles in parallel (max 2 at a time to not overwhelm)
    for (let i = 0; i < rolesToPreload.length; i += 2) {
      const batch = rolesToPreload.slice(i, i + 2);
      const promises = batch.map(async (role) => {
        const roleText = getRoleIntroText(role, narrativeTone);
        const audio = await fetchAudio(roleText, voiceType, signal);
        if (audio) {
          cacheRef.current.roles.set(role, audio);
          console.log('Preloaded role:', role);
        }
      });
      await Promise.all(promises);
    }

    isPreloadingRef.current = false;
    console.log('Preload complete');
  }, []);

  const getPreloadedIntro = useCallback((): string | null => {
    return cacheRef.current.intro?.url || null;
  }, []);

  const getPreloadedRolesIntro = useCallback((): string | null => {
    return cacheRef.current.rolesIntro?.url || null;
  }, []);

  const getPreloadedRole = useCallback((role: string): string | null => {
    return cacheRef.current.roles.get(role)?.url || null;
  }, []);

  const isPreloading = useCallback((): boolean => {
    return isPreloadingRef.current;
  }, []);

  const hasPreloadedContent = useCallback((): boolean => {
    return cacheRef.current.intro !== null || cacheRef.current.rolesIntro !== null;
  }, []);

  const cleanup = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    if (cacheRef.current.intro?.url) {
      URL.revokeObjectURL(cacheRef.current.intro.url);
    }
    if (cacheRef.current.rolesIntro?.url) {
      URL.revokeObjectURL(cacheRef.current.rolesIntro.url);
    }
    cacheRef.current.roles.forEach((audio) => {
      URL.revokeObjectURL(audio.url);
    });
    cacheRef.current = { intro: null, rolesIntro: null, roles: new Map() };
  }, []);

  return {
    preloadNarrations,
    getPreloadedIntro,
    getPreloadedRolesIntro,
    getPreloadedRole,
    isPreloading,
    hasPreloadedContent,
    cleanup,
  };
};

// Role intro texts (duplicated from RolesIntro for preloading)
function getRoleIntroText(role: string, tone: NarrativeTone): string {
  const toneTexts: Record<NarrativeTone, Record<string, string>> = {
    taquin: {
      werewolf: "Le Loup-Garou ! Le gros méchant de l'histoire ! Chaque nuit, avec ses potes loups, il bouffe un villageois. Soyez discrets les toutous !",
      villager: "Le Villageois ! Le rôle de base, le quidam, le pékin moyen ! Ton seul pouvoir ? Voter et prier pour pas te faire croquer !",
      seer: "La Voyante ! La commère du village ! Chaque nuit, elle mate le rôle d'un joueur. Mais chut, faut être subtil avec tes infos !",
      witch: "La Sorcière ! Deux potions, deux pouvoirs ! Une pour sauver, une pour buter. Utilise-les bien, y'en a qu'une de chaque !",
      hunter: "Le Chasseur ! Le gars avec le fusil ! Si tu meurs, tu peux emmener quelqu'un avec toi. Bang bang !",
      cupid: "Cupidon ! L'entremetteur du village ! Première nuit, tu désignes deux amoureux. Si l'un meurt, l'autre aussi. C'est romantique non ?",
      white_wolf: "Le Loup Blanc ! Le loup solitaire ! Tu chasses avec les loups, mais une nuit sur deux, tu peux en croquer un. Seul contre tous !",
    },
    coquin: {
      werewolf: "Le Loup-Garou ! Cette bête de sexe qui dévore les villageois la nuit ! Avec les autres loups, vous allez faire des cochonneries !",
      villager: "Le Villageois ! Bon, t'as pas de pouvoir spécial, mais t'as d'autres talents cachés on espère !",
      seer: "La Voyante ! La coquine qui regarde par le trou de la serrure ! Chaque nuit, elle voit tout d'un joueur !",
      witch: "La Sorcière ! La cougar du village avec ses potions ! Une pour rallumer la flamme, une pour l'éteindre définitivement !",
      hunter: "Le Chasseur ! Le mâle alpha ! Si on te descend, tu dégaines et tu tires sur qui tu veux !",
      cupid: "Cupidon ! Le dieu de l'amour et des coups d'un soir ! Tu crées un couple maudit, liés jusqu'à la mort !",
      white_wolf: "Le Loup Blanc ! Le bad boy des loups ! Tu joues double jeu et tu croques tout le monde !",
    },
    quebecois: {
      werewolf: "Le Loup-Garou tabarnak ! Le gros méchant de la partie ! La nuit tu manges les villageois avec tes chums loups, c'est l'fun en crisse !",
      villager: "Le Villageois mon ami ! T'as rien de spécial mais câline, tu vas survivre ! Fais de ton mieux !",
      seer: "La Voyante ma belle ! Chaque nuit tu découvres le rôle d'un joueur ! C'est malade en tabarouette !",
      witch: "La Sorcière sacrement ! Deux potions de fou ! Une pour sauver ton chum, une pour envoyer quelqu'un dans l'bois !",
      hunter: "Le Chasseur mon homme ! Si on te descend, tu sors ton fusil pis tu prends quelqu'un avec toé ! Pas de pitié icitte !",
      cupid: "Cupidon mon p'tit câlin ! Tu fais matcher deux personnes pis si l'un crève, l'autre itou ! C'est l'amour à mort !",
      white_wolf: "Le Loup Blanc le traître ! Tu manges avec les loups mais tu les manges aussi ! Double jeu de malade mental !",
    },
    ivre: {
      werewolf: "Le Loup... hic... Garou ! *burp* Tu manges des gens la nuit avec tes potes... hic... les autres loups quoi !",
      villager: "Le Villa... hic... geois ! T'as pas de pouvoir mais... *burp* ... t'es important quand même ! Je crois...",
      seer: "La Voyante... hic... elle voit tout ! Même double des fois... *burp* ... chaque nuit elle mate un joueur !",
      witch: "La Sorci... hic... ère ! Elle a des potions ! Une rouge une verte... ou l'inverse ? *burp* Une pour sauver, une pour buter !",
      hunter: "Le Chasseur ! *bang bang* hic... si tu meurs, tu tires ! Mais vise bien parce que... *burp* ... j'ai la tête qui tourne !",
      cupid: "Cupidoooon ! *hic* L'amour c'est beau ! Tu fais un couple et... *burp* ... ils meurent ensemble c'est romantique non ?",
      white_wolf: "Le Loup Blanc... hic... le traître des loups ! Tu les manges aussi ! *burp* C'est compliqué mais... hic... tu comprends quoi !",
    },
    sauvage: {
      werewolf: "Le Loup-Garou espèce de sale bête ! Tu bouffes les villageois la nuit comme le gros porc que t'es !",
      villager: "Le Villageois, le boulet de base ! T'as aucun pouvoir, t'es juste là pour te faire bouffer, pauvre naze !",
      seer: "La Voyante, la balance du village ! Tu mates les rôles des autres comme la grosse fouineuse que t'es !",
      witch: "La Sorcière, la vieille tarée aux potions ! Une pour sauver les nazes, une pour buter les emmerdeurs !",
      hunter: "Le Chasseur, le rageux avec son flingue ! Tu meurs, t'emmènes quelqu'un avec toi comme le mauvais perdant que t'es !",
      cupid: "Cupidon, le crétin qui joue les entremetteurs ! Tu colles deux débiles ensemble et ils crèvent en même temps !",
      white_wolf: "Le Loup Blanc, le traître des traîtres ! Tu bouffe tout le monde, même tes potes loups ! T'as vraiment aucune loyauté !",
    },
  };

  return toneTexts[tone]?.[role] || '';
}
